import { useState, useRef, useEffect, useCallback } from 'react';
import { gsap } from 'gsap';
import {
  Heart,
  MessageCircle,
  Share2,
  Music2,
  Volume2,
  VolumeX,
  Loader2,
  User,
} from 'lucide-react';
import { useAuth } from '@/providers/AuthProvider';
import { useBurstsFeed } from '@/hooks/useFeed';
import { useLikePost } from '@/hooks/usePost';
import { useFollowUser } from '@/hooks/useUser';
import { formatCount, timeAgo } from '@/lib/formatters';
import { getVideoSource } from '@/lib/video';
import VideoPlayer from '@/components/VideoPlayer';
import CommentModal from '@/components/CommentModal';
import { getPostUser, getPostImage, getLikesCount } from '@/components/PostCard';
import type { Post } from '@/types';

/** Get the first image URL from a post. */
function getPostThumbnail(post: Post): string {
  // Prefer video thumbnail for burst/short posts
  if (post.videoUrls?.thumbnail) return post.videoUrls.thumbnail;
  return getPostImage(post) || '';
}

export default function Shorts() {
  useAuth(); // Auth context needed for comment modal

  // Feed data
  const {
    data: feedData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isFeedLoading,
  } = useBurstsFeed();

  // Flatten paginated feed pages into a single posts array
  const posts = feedData?.pages.flatMap((page) => page.posts) ?? [];

  // UI state
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [showComments, setShowComments] = useState(false);

  // Track optimistic liked state per post ulid
  const [likedPosts, setLikedPosts] = useState<Set<string>>(new Set());

  // Track optimistic followed state per username
  const [followedUsers, setFollowedUsers] = useState<Set<string>>(new Set());

  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const shortRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Mutations
  const likeMutation = useLikePost();
  const followMutation = useFollowUser();

  // Current post
  const currentPost: Post | undefined = posts[currentIndex];

  // Sync initial liked/followed state from API data
  useEffect(() => {
    const liked = new Set<string>();
    const followed = new Set<string>();
    for (const post of posts) {
      if (post.postEngagement?.liked) liked.add(post.id);
      const u = post.user as Record<string, unknown> | undefined;
      if (u?.isFollowing && u?.username) followed.add(u.username as string);
    }
    setLikedPosts(liked);
    setFollowedUsers(followed);
    // Intentionally only runs when posts array reference changes (new pages loaded)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posts.length]);


  // IntersectionObserver to track active short + fetch next page
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            const idx = shortRefs.current.findIndex((el) => el === entry.target);
            if (idx !== -1) {
              setCurrentIndex(idx);
              // Fetch more when near end
              if (idx >= posts.length - 2 && hasNextPage && !isFetchingNextPage) {
                fetchNextPage();
              }
            }
          }
        });
      },
      { root: container, threshold: [0.5] },
    );

    shortRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [posts.length, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleLike = useCallback(
    (post: Post) => {
      const isLiked = likedPosts.has(post.id);

      // Optimistic UI update
      setLikedPosts((prev) => {
        const next = new Set(prev);
        if (isLiked) {
          next.delete(post.id);
        } else {
          next.add(post.id);
          const heartEl = document.getElementById(`heart-${post.id}`);
          if (heartEl) {
            gsap.fromTo(
              heartEl,
              { scale: 0.6, rotation: -8 },
              { scale: 1.15, rotation: 8, duration: 0.11, yoyo: true, repeat: 1, ease: 'power2.out' }
            );
          }
        }
        return next;
      });

      likeMutation.mutate({ ulid: post.id, liked: isLiked });
    },
    [likedPosts, likeMutation]
  );

  const handleFollow = useCallback(
    (username: string) => {
      const isFollowing = followedUsers.has(username);

      // Optimistic UI update
      setFollowedUsers((prev) => {
        const next = new Set(prev);
        if (isFollowing) {
          next.delete(username);
        } else {
          next.add(username);
        }
        return next;
      });

      followMutation.mutate({ username, isFollowing });
    },
    [followedUsers, followMutation]
  );

  const openComments = () => {
    setShowComments(true);
  };

  const closeComments = () => {
    setShowComments(false);
  };

  // Touch/swipe handled natively by CSS scroll-snap

  // Loading state — no posts yet
  if (isFeedLoading) {
    return (
      <div className="fixed inset-0 bg-[#0B0D10] flex items-center justify-center">
        <Loader2 size={40} className="animate-spin text-[#A38BFF]" />
      </div>
    );
  }

  // Empty state
  if (!isFeedLoading && posts.length === 0) {
    return (
      <div className="fixed inset-0 bg-[#0B0D10] flex flex-col items-center justify-center gap-3">
        <Music2 size={48} className="text-[#2A2F3A]" />
        <p className="text-[#A7B0C8]">No shorts available</p>
        <p className="text-sm text-[#A7B0C8]">Check back soon!</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="shorts-feed"
    >
      {/* Scroll-snap shorts */}
      {posts.map((post, index) => {
        const author = getPostUser(post);
        const thumbnail = getPostThumbnail(post);
        const videoSource = getVideoSource(post);
        const caption = post.content || post.title || '';
        const musicLabel = post.type === 'SONG' && post.title ? post.title : '';
        const isLiked = likedPosts.has(post.id);
        const likesCount = getLikesCount(post);
        const commentsCount = post.commentCount ?? 0;
        const sharesCount = post.postEngagement?.shareCount ?? 0;
        const isFollowing = followedUsers.has(author.username);

        return (
          <div
            key={post.id}
            ref={(el) => {
              shortRefs.current[index] = el;
            }}
            className="shorts-item"
          >
            {/* Video player fills entire snap item */}
            {videoSource ? (
              <VideoPlayer
                src={videoSource.src}
                poster={videoSource.poster || thumbnail}
                aspectRatio="9/16"
                muted={isMuted}
                className="shorts-player"
              />
            ) : thumbnail ? (
              <img
                src={thumbnail}
                alt={caption}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-[#141821] flex items-center justify-center">
                <Music2 size={64} className="text-[#2A2F3A]" />
              </div>
            )}

            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30 pointer-events-none" />

            {/* Top controls */}
            <div className="absolute top-[calc(12px+env(safe-area-inset-top,0px))] right-4 flex items-center gap-4 z-20">
              <button
                onClick={() => setIsMuted(!isMuted)}
                className="w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white"
                aria-label={isMuted ? 'Unmute' : 'Mute'}
              >
                {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
              </button>
            </div>

            {/* Right actions */}
            <div className="absolute right-3 bottom-[calc(128px+env(safe-area-inset-bottom,0px))] flex flex-col items-center gap-5 z-20">
              {/* Like */}
              <button
                id={`heart-${post.id}`}
                onClick={() => handleLike(post)}
                className="flex flex-col items-center gap-1"
                aria-label={isLiked ? 'Unlike' : 'Like'}
              >
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                    isLiked
                      ? 'bg-[#A38BFF] text-white'
                      : 'bg-black/40 backdrop-blur-sm text-white'
                  }`}
                >
                  <Heart size={24} fill={isLiked ? 'white' : 'none'} />
                </div>
                <span className="text-xs text-white font-medium">
                  {formatCount(likesCount)}
                </span>
              </button>

              {/* Comments */}
              <button
                onClick={openComments}
                className="flex flex-col items-center gap-1"
                aria-label="Open comments"
              >
                <div className="w-12 h-12 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white">
                  <MessageCircle size={24} />
                </div>
                <span className="text-xs text-white font-medium">
                  {formatCount(commentsCount)}
                </span>
              </button>

              {/* Share */}
              <button
                className="flex flex-col items-center gap-1"
                aria-label="Share"
              >
                <div className="w-12 h-12 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white">
                  <Share2 size={24} />
                </div>
                <span className="text-xs text-white font-medium">
                  {formatCount(sharesCount)}
                </span>
              </button>

              {/* Spinning thumbnail disc */}
              <button className="flex flex-col items-center gap-1" aria-label="Sound">
                <div className="w-12 h-12 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white overflow-hidden">
                  {thumbnail ? (
                    <img
                      src={thumbnail}
                      alt=""
                      className="w-full h-full object-cover animate-rotate-slow"
                    />
                  ) : (
                    <Music2 size={22} className="text-white animate-rotate-slow" />
                  )}
                </div>
              </button>
            </div>

            {/* Bottom info */}
            <div className="absolute left-4 right-20 bottom-[calc(112px+env(safe-area-inset-bottom,0px))] z-20">
              {/* Author row */}
              <div className="flex items-center gap-3 mb-3">
                {author.avatar ? (
                  <img
                    src={author.avatar}
                    alt={author.name}
                    className="w-10 h-10 rounded-full object-cover border-2 border-white/30"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-[#A38BFF]/20 border-2 border-white/30 flex items-center justify-center">
                    <User size={18} className="text-[#A38BFF]" />
                  </div>
                )}
                <span className="text-white font-semibold">
                  @{author.username || author.name}
                </span>
                {author.username && (
                  <button
                    onClick={() => handleFollow(author.username)}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      isFollowing
                        ? 'bg-white/20 text-white'
                        : 'bg-[#A38BFF] text-[#0B0D10]'
                    }`}
                  >
                    {isFollowing ? 'Following' : 'Follow'}
                  </button>
                )}
              </div>

              {/* Caption */}
              {caption ? (
                <p className="text-white text-sm mb-3 line-clamp-2">{caption}</p>
              ) : null}

              {/* Timestamp */}
              {post.createdAt ? (
                <p className="text-white/50 text-xs mb-2">{timeAgo(post.createdAt)}</p>
              ) : null}

              {/* Music pill */}
              {musicLabel ? (
                <div className="flex items-center gap-2 bg-black/40 backdrop-blur-sm rounded-full px-3 py-2 w-fit">
                  <Music2 size={16} className="text-white animate-rotate-slow" />
                  <span className="text-white text-sm">{musicLabel}</span>
                </div>
              ) : null}
            </div>
          </div>
        );
      })}

      {/* Infinite scroll loading indicator */}
      {isFetchingNextPage && (
        <div className="shorts-item flex items-center justify-center">
          <Loader2 size={20} className="animate-spin text-[#A38BFF]" />
        </div>
      )}

      {/* Comments Modal */}
      {showComments && currentPost && (
        <CommentModal
          postUlid={currentPost.id}
          onClose={closeComments}
        />
      )}
    </div>
  );
}
